'use client';
import { CatGrid } from '@blocks-react/components/Grid';
import { CatGridItem } from '@blocks-react/components/GridItem';
import { CatButton } from '@blocks-react/components/Button';
import AddressFormInput from './Guest-Input-Form-Text';
import { FORM_NAMES, FORM_LABELS, INPUTS_MAX_LENGTH } from '../constants';
import { GuestDropDownForm } from './Guest-Drop-Down-Form';
import { getFormRules, onSelectedCountry } from './utils';
import { FormFieldsProps } from './types';
import { BlocksSelectEvent } from '@blocks-web-components/events';
import { useFormContext } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { Conditional } from '../../common';

const AddressInformation = ({
  countries = [],
  addressLine2Isrequired = '',
  t
}: FormFieldsProps) => {
  const formRules = getFormRules(t);
  const {
    watch,
    formState: { isSubmitting, errors },
    setValue,
    clearErrors
  } = useFormContext();
  const prevCountryCode = useRef<string>('');
  const isFirstTimeGuestModalOpens = useRef(true);
  const countryCode = watch('country');
  const stateList = countries?.find(
    (country) => country.code === countryCode
  )?.states;
  const [showAddress2, setShowAddress2] = useState(false);
  const isStreetAddress2 = watch('streetAddress2');
  useEffect(() => {
    if (
      isStreetAddress2 !== '' ||
      (isStreetAddress2 === '' && !!addressLine2Isrequired)
    ) {
      setShowAddress2(true);
    }
  }, [isStreetAddress2]);
  useEffect(() => {
    if (isFirstTimeGuestModalOpens.current) {
      prevCountryCode.current = countryCode;
      isFirstTimeGuestModalOpens.current = false;
      return;
    }

    if (countryCode && prevCountryCode.current !== countryCode) {
      clearErrors(FORM_NAMES.STATE);
      setValue(FORM_NAMES.STATE, '');
    }
    prevCountryCode.current = countryCode;
  }, [clearErrors, countryCode, setValue]);
  const onSelectedState = (e: BlocksSelectEvent) => {
    const selectedStateCode = e.detail.value;
    const selectedState = stateList?.find(
      (location) => location.code === selectedStateCode
    );
    setValue('state', selectedState?.code);
  };

  const displayStatesAsDropdown = !!stateList?.length;
  return (
    <>
      <p className="cat-u-theme-typography-label-lg u-color--Black">
        {t('PUD_GUEST_ADDRESS_INFO')}
      </p>
      <CatGrid gap="xs">
        <CatGridItem>
          <GuestDropDownForm
            fieldId={'country'}
            name={FORM_NAMES.COUNTRY}
            rules={formRules[FORM_NAMES.COUNTRY]}
            disabled={isSubmitting}
            dropdownOptions={countries}
            onSelectDropdown={(e) =>
              onSelectedCountry(e, countries ?? [], setValue)
            }
            placeholder={t('SELECT_AN_OPTION')}
            label={t(FORM_LABELS[FORM_NAMES.COUNTRY])}
            required
            error={errors?.country}
            isTypeahead
            t={t}
          />
        </CatGridItem>
        <CatGridItem>
          <AddressFormInput
            name={FORM_NAMES.ZIP_CODE}
            label={t(FORM_LABELS[FORM_NAMES.ZIP_CODE])}
            required
            error={errors?.zipCode}
            maxlength={INPUTS_MAX_LENGTH.zipCode}
            rules={formRules[FORM_NAMES.ZIP_CODE]}
            t={t}
          />
        </CatGridItem>
        <CatGridItem>
          <AddressFormInput
            name={FORM_NAMES.STREET_ADDRESS}
            label={t(FORM_LABELS[FORM_NAMES.STREET_ADDRESS])}
            required
            error={errors?.streetAddress}
            maxlength={INPUTS_MAX_LENGTH.address1}
            rules={formRules[FORM_NAMES.STREET_ADDRESS]}
            t={t}
          />
        </CatGridItem>
        <Conditional test={!showAddress2}>
          <div className="ms-n3 mt-n2">
            <CatGridItem>
              <CatButton onClick={() => setShowAddress2(true)} variant="ghost">
                {t('ADD_ADDRESS_LINE_2')}
              </CatButton>
            </CatGridItem>
          </div>
        </Conditional>

        <Conditional test={showAddress2}>
          <CatGridItem>
            <AddressFormInput
              name={FORM_NAMES.STREET_ADDRESS2}
              label={t(FORM_LABELS[FORM_NAMES.STREET_ADDRESS2])}
              error={errors?.streetAddress2}
              required
              maxlength={INPUTS_MAX_LENGTH.address2}
              rules={formRules[FORM_NAMES.STREET_ADDRESS2]}
              t={t}
            />
          </CatGridItem>
        </Conditional>
        <CatGridItem>
          <AddressFormInput
            name={FORM_NAMES.CITY}
            label={t(FORM_LABELS[FORM_NAMES.CITY])}
            required
            error={errors?.city}
            maxlength={INPUTS_MAX_LENGTH.city}
            rules={formRules[FORM_NAMES.CITY]}
            t={t}
          />
        </CatGridItem>
        {displayStatesAsDropdown ? (
          <CatGridItem>
            <GuestDropDownForm
              rules={formRules[FORM_NAMES.STATE]}
              dropdownOptions={stateList ?? []}
              onSelectDropdown={onSelectedState}
              fieldId={'state'}
              name={FORM_NAMES.STATE}
              placeholder={t('PLEASE_SELECT')}
              error={errors?.state}
              label={t(FORM_LABELS[FORM_NAMES.STATE])}
              isTypeahead
              required
              t={t}
            />
          </CatGridItem>
        ) : (
          <CatGridItem>
            <AddressFormInput
              name={FORM_NAMES.STATE}
              label={t(FORM_LABELS[FORM_NAMES.STATE])}
              disabled={isSubmitting}
              maxlength={40}
              required
              error={errors?.state}
              rules={formRules[FORM_NAMES.STATE]}
              t={t}
            />
          </CatGridItem>
        )}
      </CatGrid>
    </>
  );
};

export default AddressInformation;
